import { useState } from "react";
import { Eye, EyeOff, Lock, ShieldCheck, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/Button";
import { appLogin } from "../api";

const HIGHLIGHTS = [
  { icon: TrendingUp, title: "Live autopilot", copy: "Every active rule runs concurrently with its own exits." },
  { icon: Sparkles, title: "Research labs", copy: "Backtest rules and models on real option premiums." },
  { icon: ShieldCheck, title: "Paper first", copy: "Paper and live execution are never one click apart." },
];

export default function Login({ onLogin }) {
  const [pin, setPin] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const r = await appLogin(pin);
      localStorage.setItem("appToken", r.data.token);
      localStorage.setItem("appRole", r.data.role || "admin");
      onLogin();
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed — check the access code and try again.");
      setPin("");
    }
    setLoading(false);
  };

  return (
    <div className="app-canvas flex min-h-screen items-center justify-center p-4">
      <div className="grid w-full max-w-4xl overflow-hidden rounded-3xl border border-line bg-surface shadow-lg lg:grid-cols-[1.05fr_1fr]">
        {/* Brand side */}
        <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-cyan-500/[.14] via-surface to-blue-600/[.12] p-10 lg:flex">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-cyan-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 text-xl font-black text-white shadow-[0_18px_40px_-16px_rgb(var(--c-cyan-500)/.9)]">
                ∞
              </div>
              <div>
                <p className="text-lg font-bold tracking-tight text-hi">Infinity</p>
                <p className="text-[10px] font-bold uppercase tracking-eyebrow text-low">Options intelligence</p>
              </div>
            </div>
            <h1 className="mt-10 max-w-sm text-3xl font-bold leading-tight tracking-tight text-hi">
              Rules, research and execution in one console.
            </h1>
          </div>

          <ul className="relative mt-10 space-y-4">
            {HIGHLIGHTS.map(({ icon: Icon, title, copy }) => (
              <li key={title} className="flex gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border border-cyan-500/25 bg-cyan-500/[.1] text-cyan-300">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-hi">{title}</p>
                  <p className="text-xs leading-relaxed text-low">{copy}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Form side */}
        <div className="p-8 sm:p-10">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-blue-600 text-lg font-black text-white">∞</div>
            <div>
              <p className="text-base font-bold tracking-tight text-hi">Infinity</p>
              <p className="text-[10px] font-bold uppercase tracking-eyebrow text-low">Options intelligence</p>
            </div>
          </div>

          <h2 className="text-xl font-bold tracking-tight text-hi">Welcome back</h2>
          <p className="mt-1 text-sm text-low">Enter your access code to open the panel.</p>

          <form onSubmit={handleSubmit} className="mt-7 space-y-4">
            <label className="block">
              <span className="mb-1.5 block text-[10px] font-bold uppercase tracking-label text-low">Access code</span>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-faint" />
                <input
                  type={show ? "text" : "password"}
                  value={pin}
                  onChange={(e) => { setPin(e.target.value); setError(""); }}
                  placeholder="••••••"
                  autoFocus
                  autoComplete="current-password"
                  maxLength={20}
                  aria-invalid={!!error}
                  className={`h-12 w-full rounded-xl pl-10 pr-11 text-center text-lg tracking-[0.35em] placeholder:tracking-[0.2em] ${
                    error ? "!border-rose-500/70" : ""
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? "Hide access code" : "Show access code"}
                  className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-faint transition-colors hover:text-hi"
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </label>

            {error && (
              <p role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/[.1] px-3 py-2 text-xs font-medium text-rose-300">
                {error}
              </p>
            )}

            <Button type="submit" variant="primary" size="lg" disabled={loading || !pin} loading={loading} className="w-full">
              {loading ? "Authenticating…" : "Open panel"}
            </Button>
          </form>

          <p className="mt-6 text-center text-[11px] text-faint">
            Sessions expire automatically — the panel signs you out when the token lapses.
          </p>
        </div>
      </div>
    </div>
  );
}
